import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, type TaskFilters } from './lib/api';
import type {
  ActionConnection,
  ActionProviderId,
  ActionProviderInfo,
  AuthUser,
  Connection,
  Insight,
  LinkType,
  Meta,
  NotificationPrefs,
  OnboardingState,
  OrgPriority,
  Profile,
  Project,
  ProviderInfo,
  SortKey,
  Status,
  Task,
  TaskCounts,
} from './types';
import { STATUSES, STATUS_LABELS } from './types';
import { TaskTable } from './components/TaskTable';
import { TaskEditor, type TaskDraft } from './components/TaskEditor';
import { CreateExternalTaskModal } from './components/CreateExternalTaskModal';
import { ConnectionsPanel } from './components/ConnectionsPanel';
import { ConnectionForm } from './components/ConnectionForm';
import { IntegrationsPanel } from './components/IntegrationsPanel';
import { BrandMark } from './components/BrandMark';
import { SettingsPanel } from './components/SettingsPanel';
import { ProjectSelect } from './components/ProjectSelect';
import { OnboardingTour } from './components/OnboardingTour';
import { AuthScreen } from './components/AuthScreen';
import { PaywallScreen } from './components/PaywallScreen';
import { BillingPanel } from './components/BillingPanel';
import { AdminPanel } from './components/AdminPanel';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { ToastStack, useToasts } from './components/Toasts';

const DEFAULT_FILTERS: TaskFilters = {
  sort: 'priority',
  dir: 'desc',
  search: '',
  status: 'all',
  connectionId: 'all',
  projectId: 'all',
  includeDone: false,
  includeArchived: false,
  includeSnoozed: false,
  needsTriage: false,
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: '',
  dueFrom: '',
  dueTo: '',
};

type Tab = 'tasks' | 'connections' | 'integrations' | 'settings' | 'billing' | 'admin';

/**
 * The session gate. Every account's data lives behind a signed session
 * cookie now, so the whole workspace waits on knowing who — if anyone — is
 * signed in before it renders or fetches anything.
 */
export default function App() {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  const [authNotice, setAuthNotice] = useState<{ kind: 'ok' | 'error'; message: string } | null>(null);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  const refreshUser = useCallback(() => api.me().then(setUser).catch(() => setUser(null)), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingResult = params.get('billing');
    const verified = params.get('verified');
    const verifyError = params.get('verifyError');
    if (verified) {
      setAuthNotice({ kind: 'ok', message: 'Email verified.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (verifyError) {
      setAuthNotice({ kind: 'error', message: verifyError });
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (!billingResult) {
      refreshUser();
      return;
    }
    // Coming back from Stripe Checkout or the billing portal — the webhook
    // that actually applies the new subscription state can lag the redirect
    // by a second or two, so poll briefly rather than trusting the very
    // first fetch to already reflect it.
    window.history.replaceState({}, '', window.location.pathname);
    let attempts = 0;
    const poll = () => {
      attempts += 1;
      api
        .me()
        .then((u) => {
          setUser(u);
          if (billingResult === 'success' && u && !u.billing.hasAccess && attempts < 5) {
            window.setTimeout(poll, 1500);
          }
        })
        .catch(() => setUser(null));
    };
    poll();
  }, [refreshUser]);

  async function stopImpersonation() {
    setStoppingImpersonation(true);
    try {
      setUser(await api.stopImpersonation());
    } catch {
      // Cookies may already be in a half-restored state — a full refresh is
      // the safe fallback either way.
      refreshUser();
    } finally {
      setStoppingImpersonation(false);
    }
  }

  if (user === undefined) {
    return (
      <div className="auth-screen">
        <span className="spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <AuthScreen
        onSignedIn={setUser}
        signup={api.signup}
        login={api.login}
        forgotPassword={api.forgotPassword}
        resetPassword={api.resetPassword}
      />
    );
  }

  const banner = user.impersonating && (
    <ImpersonationBanner
      adminEmail={user.impersonating.adminEmail}
      targetEmail={user.email}
      onStop={stopImpersonation}
      busy={stoppingImpersonation}
    />
  );

  if (!user.billing.hasAccess) {
    return (
      <>
        {banner}
        <PaywallScreen
          email={user.email}
          billing={user.billing}
          checkout={api.billingCheckout}
          fetchPlans={api.billingPlans}
          onSignOut={async () => {
            await api.logout().catch(() => {});
            setUser(null);
          }}
        />
      </>
    );
  }

  return (
    <>
      {banner}
      <Workspace user={user} onLogout={() => setUser(null)} onUserChange={setUser} initialNotice={authNotice} />
    </>
  );
}

function Workspace({
  user,
  onLogout,
  onUserChange,
  initialNotice,
}: {
  user: AuthUser;
  onLogout: () => void;
  onUserChange: (user: AuthUser) => void;
  initialNotice: { kind: 'ok' | 'error'; message: string } | null;
}) {
  const { toasts, push, dismiss } = useToasts();

  const [tab, setTab] = useState<Tab>('tasks');
  const [meta, setMeta] = useState<Meta | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [notifications, setNotifications] = useState<NotificationPrefs | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [actionProviders, setActionProviders] = useState<ActionProviderInfo[]>([]);
  const [actionConnections, setActionConnections] = useState<ActionConnection[]>([]);
  const [busyActionId, setBusyActionId] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [priorities, setPriorities] = useState<OrgPriority[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [counts, setCounts] = useState<TaskCounts>({
    total: 0,
    open: 0,
    overdue: 0,
    manual: 0,
    needsTriage: 0,
    promised: 0,
    blocking: 0,
  });
  const [filters, setFilters] = useState<TaskFilters>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [busyConnectionId, setBusyConnectionId] = useState<string | null>(null);

  const [editorTask, setEditorTask] = useState<Task | null | undefined>(undefined);
  const [savingTask, setSavingTask] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [taskWarnings, setTaskWarnings] = useState<string[]>([]);
  const [createExternalOpen, setCreateExternalOpen] = useState(false);

  const [connFormOpen, setConnFormOpen] = useState(false);
  const [connBeingEdited, setConnBeingEdited] = useState<Connection | null>(null);

  const [onboarding, setOnboarding] = useState<OnboardingState | null>(null);
  const [tourOpen, setTourOpen] = useState(false);

  const fail = useCallback(
    (err: unknown) => push('error', err instanceof ApiError ? err.message : String(err)),
    [push]
  );

  const loadTasks = useCallback(
    async (next: TaskFilters) => {
      setLoading(true);
      try {
        const data = await api.tasks(next);
        setTasks(data.tasks);
        setCounts(data.counts);
      } catch (err) {
        fail(err);
      } finally {
        setLoading(false);
      }
    },
    [fail]
  );

  const loadSupporting = useCallback(async () => {
    try {
      const [conns, projs, prios, ins] = await Promise.all([
        api.connections(),
        api.projects(),
        api.priorities(),
        api.insights(),
      ]);
      setConnections(conns);
      setProjects(projs);
      setPriorities(prios);
      setInsights(ins);
    } catch (err) {
      fail(err);
    }
  }, [fail]);

  // Shared by the Priorities tab's project form and every inline
  // "+ New project…" (task filter, task editor) — one place that actually
  // calls the API and refreshes the project list, so all three stay in sync
  // and only differ in what they do with the created project afterward.
  const createProject = useCallback(
    async (name: string, orgPriorityId: string | null = null) => {
      try {
        const project = await api.createProject({ name, orgPriorityId });
        await loadSupporting();
        return project;
      } catch (err) {
        fail(err);
        return undefined;
      }
    },
    [fail, loadSupporting]
  );

  const loadActionConnections = useCallback(async () => {
    try {
      setActionConnections(await api.actionConnections());
    } catch (err) {
      fail(err);
    }
  }, [fail]);

  useEffect(() => {
    api.meta().then(setMeta).catch(fail);
    api.profile().then(setProfile).catch(fail);
    api.providers().then(setProviders).catch(fail);
    api.actions().then(setActionProviders).catch(fail);
    api.notificationPrefs().then(setNotifications).catch(fail);
    api
      .onboarding()
      .then((state) => {
        setOnboarding(state);
        if (!state.completed) setTourOpen(true);
      })
      .catch(fail);
    loadSupporting();
    loadActionConnections();
  }, [fail, loadSupporting, loadActionConnections]);

  // Shows once, for whichever query-param the app was loaded with
  // (?verified=1 or ?verifyError=...) — App() already scrubs the URL, this
  // just surfaces it as a toast now that the toast system exists.
  useEffect(() => {
    if (initialNotice) push(initialNotice.kind, initialNotice.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function finishOnboarding() {
    setTourOpen(false);
    api.updateOnboarding(true).then(setOnboarding).catch(fail);
  }

  function changeNotifications(patch: Partial<NotificationPrefs>) {
    setNotifications((current) => (current ? { ...current, ...patch } : current));
    api.updateNotificationPrefs(patch).then(setNotifications).catch(fail);
  }

  async function resendVerification() {
    setResendingVerification(true);
    try {
      await api.resendVerification();
      push('ok', 'Verification email sent.');
    } catch (err) {
      fail(err);
    } finally {
      setResendingVerification(false);
    }
  }

  async function impersonate(id: string) {
    try {
      onUserChange(await api.admin.impersonate(id));
      push('ok', 'Now viewing as that user.');
    } catch (err) {
      fail(err);
    }
  }

  /** The OAuth popup posts a message here when the provider's consent screen finishes. */
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if ((event.data as { source?: string })?.source !== 'tasker-oauth') return;
      const ok = (event.data as { ok?: boolean }).ok;
      push(ok ? 'ok' : 'error', ok ? 'Connected.' : 'Could not connect — see the pop-up window for details.');
      setBusyActionId(null);
      loadActionConnections();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [push, loadActionConnections]);

  /** Same idea, but for a system connection (Systems tab) connecting by sign-in. */
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { source?: string; ok?: boolean; id?: string; name?: string };
      if (data?.source !== 'tasker-connection-oauth') return;
      if (!data.ok) {
        push('error', 'Could not connect — see the pop-up window for details.');
        return;
      }
      setConnFormOpen(false);
      push('ok', `Connected “${data.name}”.`);
      (async () => {
        await loadSupporting();
        if (data.id) {
          setBusyConnectionId(data.id);
          try {
            const report = await api.syncConnection(data.id);
            push(
              report.ok ? 'ok' : 'error',
              report.ok
                ? `Pulled ${report.fetched} item(s) from ${data.name}.`
                : `${data.name}: ${report.error}`
            );
          } catch (err) {
            fail(err);
          } finally {
            setBusyConnectionId(null);
          }
          await Promise.all([loadSupporting(), loadTasks(filters)]);
        }
      })();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [push, fail, loadSupporting, loadTasks, filters]);

  useEffect(() => {
    const delay = filters.search ? 250 : 0;
    const handle = setTimeout(() => loadTasks(filters), delay);
    return () => clearTimeout(handle);
  }, [filters, loadTasks]);

  const setFilter = <K extends keyof TaskFilters>(key: K, value: TaskFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const onSort = (key: SortKey) =>
    setFilters((f) => ({
      ...f,
      sort: key,
      // Priority and age read best highest-first; everything else lowest-first.
      dir: f.sort === key ? (f.dir === 'asc' ? 'desc' : 'asc') : key === 'priority' || key === 'age' ? 'desc' : 'asc',
    }));

  /* ------------------------------------------------------------- profile */

  const profileTimer = useRef<number | undefined>(undefined);

  /** Sliders update locally at once and persist on a short debounce. */
  const changeProfile = useCallback(
    (patch: Record<string, unknown>) => {
      setProfile((current) => (current ? ({ ...current, ...patch } as Profile) : current));
      window.clearTimeout(profileTimer.current);
      profileTimer.current = window.setTimeout(async () => {
        try {
          const saved = await api.updateProfile(patch);
          setProfile(saved);
          await loadTasks(filters);
        } catch (err) {
          fail(err);
        }
      }, 300);
    },
    [fail, filters, loadTasks]
  );

  /* --------------------------------------------------------------- sync */

  async function syncAll() {
    setSyncing(true);
    try {
      const reports = await api.syncAll();
      if (!reports.length) {
        push('info', 'No enabled connections to sync.');
      } else {
        const failed = reports.filter((r) => !r.ok);
        const created = reports.reduce((n, r) => n + r.created, 0);
        const updated = reports.reduce((n, r) => n + r.updated, 0);
        const derived = reports.reduce((n, r) => n + r.derived, 0);
        failed.forEach((r) => push('error', `${r.connectionName}: ${r.error}`));
        if (failed.length < reports.length) {
          push(
            'ok',
            `Synced ${reports.length - failed.length} system(s): ${created} new, ${updated} updated` +
              (derived ? `, ${derived} attribute${derived === 1 ? '' : 's'} filled in from the source.` : '.')
          );
        }
      }
      await Promise.all([loadTasks(filters), loadSupporting()]);
    } catch (err) {
      fail(err);
    } finally {
      setSyncing(false);
    }
  }

  /* --------------------------------------------------------------- tasks */

  async function openTask(task: Task) {
    setTaskError(null);
    setTaskWarnings([]);
    setEditorTask(task);
    try {
      // Opening is itself a signal — it is what "never looked at it" measures.
      setEditorTask(await api.viewTask(task.id));
    } catch {
      /* the editor is already open with what we had; a lost view log is harmless */
    }
  }

  function draftToBody(draft: TaskDraft) {
    return {
      title: draft.title.trim(),
      description: draft.description.trim() || null,
      dueDate: draft.dueDate || null,
      status: draft.status,
      priority: draft.priority ? Number(draft.priority) : null,
      importance: draft.importance ? (Number(draft.importance) as 1 | 2 | 3 | 4) : null,
      effort: draft.effort || null,
      urgencyOverride: draft.urgencyOverride ? (Number(draft.urgencyOverride) as 1 | 2 | 3 | 4) : null,
      impactType: draft.impactType || null,
      impactScope: draft.impactScope ? (Number(draft.impactScope) as 1 | 2 | 3 | 4) : null,
      blockingOthers: draft.blockingOthers,
      blockingNote: draft.blockingNote.trim() || null,
      commitmentTo: draft.commitmentTo.trim() || null,
      commitmentDue: draft.commitmentDue || null,
      projectId: draft.projectId || null,
    };
  }

  async function saveTask(draft: TaskDraft) {
    setSavingTask(true);
    setTaskError(null);
    setTaskWarnings([]);
    try {
      if (editorTask) {
        const result = await api.updateTask(editorTask.id, draftToBody(draft));
        if (result.warnings.length) {
          setTaskWarnings(result.warnings);
          result.warnings.forEach((w) => push('warn', w));
        }
        push('ok', result.pushed ? `Updated in ${editorTask.systemLabel}.` : 'Task saved.');
      } else {
        await api.createTask(draftToBody(draft));
        push('ok', 'Task added.');
      }
      setEditorTask(undefined);
      await Promise.all([loadTasks(filters), loadSupporting()]);
    } catch (err) {
      setTaskError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSavingTask(false);
    }
  }

  async function onExternalTaskCreated(task: Task) {
    setCreateExternalOpen(false);
    push('ok', `Created in ${task.systemLabel}.`);
    await Promise.all([loadTasks(filters), loadSupporting()]);
    // Open it right away so the "Open in source" link is immediately visible
    // — the whole point of creating it here instead of over in the source system.
    setEditorTask(task);
  }

  /** Small task mutations that keep the editor open and refresh in place. */
  async function mutateOpenTask(fn: () => Promise<Task>, message?: string) {
    if (!editorTask) return;
    setSavingTask(true);
    try {
      setEditorTask(await fn());
      if (message) push('ok', message);
      await loadTasks(filters);
    } catch (err) {
      setTaskError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSavingTask(false);
    }
  }

  async function removeTask() {
    if (!editorTask) return;
    setSavingTask(true);
    try {
      const wasLinked = Boolean(editorTask.connectionId);
      const systemLabel = editorTask.systemLabel;
      await api.deleteTask(editorTask.id);
      setEditorTask(undefined);
      push('ok', wasLinked ? `Removed from your list. Still in ${systemLabel}.` : 'Task deleted.');
      await loadTasks(filters);
    } catch (err) {
      setTaskError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSavingTask(false);
    }
  }

  async function connectConnectionOAuth(provider: ProviderInfo) {
    try {
      const url = await api.authorizeConnection(provider.id);
      const popup = window.open(url, 'tasker-oauth', 'width=520,height=680');
      if (!popup) {
        push('error', 'Your browser blocked the pop-up. Allow pop-ups for this site and try again.');
      }
      // The connection form was already closed by the caller; the "message"
      // listener above picks up the result when the popup finishes.
    } catch (err) {
      fail(err);
    }
  }

  async function withConnection(c: Connection, fn: () => Promise<void>) {
    setBusyConnectionId(c.id);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusyConnectionId(null);
    }
  }

  /* ---------------------------------------------------------------- actions */

  async function connectActionForm(provider: ActionProviderInfo) {
    setBusyActionId(provider.id);
    try {
      await api.createActionConnection({ provider: provider.id, name: provider.label });
      push('ok', `Connected ${provider.label}.`);
      await loadActionConnections();
    } catch (err) {
      fail(err);
    } finally {
      setBusyActionId(null);
    }
  }

  async function connectActionOAuth(provider: ActionProviderInfo) {
    setBusyActionId(provider.id);
    try {
      const url = await api.authorizeAction(provider.id);
      const popup = window.open(url, 'tasker-oauth', 'width=520,height=680');
      if (!popup) {
        push('error', 'Your browser blocked the pop-up. Allow pop-ups for this site and try again.');
        setBusyActionId(null);
      }
      // busyActionId clears when the "message" listener hears back from the popup.
    } catch (err) {
      fail(err);
      setBusyActionId(null);
    }
  }

  async function withAction(id: string, fn: () => Promise<void>) {
    setBusyActionId(id);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusyActionId(null);
    }
  }

  const docProviders = useMemo(
    () =>
      actionConnections
        .filter((c) => c.enabled)
        .map((c) => ({ id: c.provider, label: c.name })),
    [actionConnections]
  );

  const systemOptions = useMemo(
    () => [
      { value: 'all', label: 'All systems' },
      { value: 'manual', label: 'Manual' },
      ...connections.map((c) => ({ value: c.id, label: c.name })),
    ],
    [connections]
  );

  const filtersDirty = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  async function signOut() {
    try {
      await api.logout();
    } catch {
      /* the cookie is cleared server-side either way; falling through to onLogout is fine */
    }
    onLogout();
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <BrandMark size={26} />
          <div className="brand-word">
            <span className="wordmark">Z3NTRAFY</span>
            <small>work across your systems</small>
          </div>
        </div>
        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'tasks'} onClick={() => setTab('tasks')}>
            My tasks
          </button>
          <button role="tab" aria-selected={tab === 'connections'} onClick={() => setTab('connections')}>
            Systems {connections.length > 0 && `(${connections.length})`}
          </button>
          <button role="tab" aria-selected={tab === 'integrations'} onClick={() => setTab('integrations')}>
            Integrations {actionConnections.length > 0 && `(${actionConnections.length})`}
          </button>
          <button role="tab" aria-selected={tab === 'settings'} onClick={() => setTab('settings')}>
            Priorities
          </button>
          <button role="tab" aria-selected={tab === 'billing'} onClick={() => setTab('billing')}>
            Billing
          </button>
          {user.isAdmin && (
            <button role="tab" aria-selected={tab === 'admin'} onClick={() => setTab('admin')}>
              Admin
            </button>
          )}
        </div>
        <div className="spacer" />
        {user.billing.status === 'trialing' && user.billing.daysLeftInTrial !== null && (
          <button className="trial-chip" onClick={() => setTab('billing')}>
            {user.billing.daysLeftInTrial > 0 ? `${user.billing.daysLeftInTrial}d left in trial` : 'Trial ends today'}
          </button>
        )}
        <button className="btn ghost" onClick={() => setTourOpen(true)}>
          ? Tour
        </button>
        <button className="btn" onClick={syncAll} disabled={syncing}>
          {syncing ? <span className="spin" /> : '⟳'} Sync all
        </button>
        <button className="btn primary" onClick={() => setEditorTask(null)}>
          + New task
        </button>
        {connections.some((c) => c.enabled && providers.find((p) => p.id === c.provider)?.supportsCreate) && (
          <button className="btn" onClick={() => setCreateExternalOpen(true)}>
            + From system
          </button>
        )}
        <div className="user-menu">
          {user.avatarUrl && <img className="avatar" src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />}
          <span className="email">{user.name ?? user.email}</span>
          <button className="btn ghost sm" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <main>
        {!user.emailVerified && (
          <div className="verify-banner">
            <span>Verify your email to make sure you can recover your account and receive task notifications.</span>
            <button className="btn ghost sm" disabled={resendingVerification} onClick={resendVerification}>
              {resendingVerification ? <span className="spin" /> : null}
              Resend email
            </button>
          </div>
        )}

        {tab === 'tasks' && (
          <>
            <div className="stats">
              <div className="stat">
                <div className="n">{counts.open}</div>
                <div className="l">Open</div>
              </div>
              <div className={`stat ${counts.overdue ? 'alarm' : ''}`}>
                <div className="n">{counts.overdue}</div>
                <div className="l">Overdue</div>
              </div>
              <div className="stat">
                <div className="n">{counts.blocking}</div>
                <div className="l">Others waiting</div>
              </div>
              <div className="stat">
                <div className="n">{counts.promised}</div>
                <div className="l">Promised</div>
              </div>
              <div className="stat">
                <div className="n">{counts.needsTriage}</div>
                <div className="l">Untriaged</div>
              </div>
            </div>

            {insights.length > 0 && (
              <div className="insights">
                {insights.map((i, idx) => (
                  <div className="insight" key={idx}>
                    <strong>{i.label}</strong>
                    <span>{i.detail}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="filters">
              <input
                className="search"
                type="search"
                placeholder="Search titles, descriptions, and IDs…"
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
              />
              <div className="field-inline">
                <span className="filter-label">System</span>
                <select
                  aria-label="Filter by system"
                  value={filters.connectionId}
                  onChange={(e) => setFilter('connectionId', e.target.value)}
                >
                  {systemOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <ProjectSelect
                ariaLabel="Filter by project"
                projects={projects}
                value={filters.projectId}
                onChange={(id) => setFilter('projectId', id)}
                onCreateProject={(name) => createProject(name)}
                leadingOptions={[
                  { value: 'all', label: 'All projects' },
                  { value: 'none', label: 'No project' },
                ]}
              />
              <select value={filters.status} onChange={(e) => setFilter('status', e.target.value as Status | 'all')}>
                <option value="all">Any status</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
              <div className="field-inline date-range">
                <span className="filter-label">Created</span>
                <input
                  type="date"
                  aria-label="Created from"
                  value={filters.createdFrom}
                  max={filters.createdTo || undefined}
                  onChange={(e) => setFilter('createdFrom', e.target.value)}
                />
                <span className="filter-sep">–</span>
                <input
                  type="date"
                  aria-label="Created to"
                  value={filters.createdTo}
                  min={filters.createdFrom || undefined}
                  onChange={(e) => setFilter('createdTo', e.target.value)}
                />
              </div>
              <div className="field-inline date-range">
                <span className="filter-label">Updated</span>
                <input
                  type="date"
                  aria-label="Updated from"
                  value={filters.updatedFrom}
                  max={filters.updatedTo || undefined}
                  onChange={(e) => setFilter('updatedFrom', e.target.value)}
                />
                <span className="filter-sep">–</span>
                <input
                  type="date"
                  aria-label="Updated to"
                  value={filters.updatedTo}
                  min={filters.updatedFrom || undefined}
                  onChange={(e) => setFilter('updatedTo', e.target.value)}
                />
              </div>
              <div className="field-inline date-range">
                <span className="filter-label">Deadline</span>
                <input
                  type="date"
                  aria-label="Deadline from"
                  value={filters.dueFrom}
                  max={filters.dueTo || undefined}
                  onChange={(e) => setFilter('dueFrom', e.target.value)}
                />
                <span className="filter-sep">–</span>
                <input
                  type="date"
                  aria-label="Deadline to"
                  value={filters.dueTo}
                  min={filters.dueFrom || undefined}
                  onChange={(e) => setFilter('dueTo', e.target.value)}
                />
              </div>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.needsTriage}
                  onChange={(e) => setFilter('needsTriage', e.target.checked)}
                />
                Needs triage
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.includeDone}
                  onChange={(e) => setFilter('includeDone', e.target.checked)}
                />
                Done
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.includeSnoozed}
                  onChange={(e) => setFilter('includeSnoozed', e.target.checked)}
                />
                Snoozed
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.includeArchived}
                  onChange={(e) => setFilter('includeArchived', e.target.checked)}
                />
                Archived
              </label>
              {filtersDirty && (
                <button className="btn ghost sm" onClick={() => setFilters(DEFAULT_FILTERS)}>
                  Reset
                </button>
              )}
            </div>

            <div className="card">
              <TaskTable
                tasks={tasks}
                sort={filters.sort}
                dir={filters.dir}
                onSort={onSort}
                onOpen={openTask}
                loading={loading}
                meta={meta}
              />
            </div>
          </>
        )}

        {tab === 'connections' && (
          <ConnectionsPanel
            connections={connections}
            providers={providers}
            busyId={busyConnectionId}
            onAdd={() => {
              setConnBeingEdited(null);
              setConnFormOpen(true);
            }}
            onEdit={(c) => {
              setConnBeingEdited(c);
              setConnFormOpen(true);
            }}
            onTest={(c) =>
              withConnection(c, async () => {
                const result = await api.testConnection(c.id);
                push(result.ok ? 'ok' : 'error', result.message);
              })
            }
            onSync={(c) =>
              withConnection(c, async () => {
                const report = await api.syncConnection(c.id);
                push(
                  report.ok ? 'ok' : 'error',
                  report.ok
                    ? `${report.connectionName}: ${report.fetched} item(s) — ${report.created} new, ${report.updated} updated, ${report.archived} archived.`
                    : `${report.connectionName}: ${report.error}`
                );
                await Promise.all([loadSupporting(), loadTasks(filters)]);
              })
            }
            onToggle={(c) =>
              withConnection(c, async () => {
                await api.updateConnection(c.id, { name: c.name, config: c.config, enabled: !c.enabled });
                await loadSupporting();
              })
            }
            onDelete={(c) =>
              withConnection(c, async () => {
                await api.deleteConnection(c.id);
                push('ok', `Removed “${c.name}”.`);
                await Promise.all([loadSupporting(), loadTasks(filters)]);
              })
            }
          />
        )}

        {tab === 'integrations' && (
          <IntegrationsPanel
            providers={actionProviders}
            connections={actionConnections}
            busyId={busyActionId}
            onConnectForm={connectActionForm}
            onConnectOAuth={connectActionOAuth}
            onSaveConfig={(c, config) =>
              withAction(c.id, async () => {
                await api.updateActionConnection(c.id, { config });
                push('ok', 'Saved.');
                await loadActionConnections();
              })
            }
            onTest={(c) =>
              withAction(c.id, async () => {
                const result = await api.testActionConnection(c.id);
                push(result.ok ? 'ok' : 'error', result.message);
              })
            }
            onDelete={(c) =>
              withAction(c.id, async () => {
                await api.deleteActionConnection(c.id);
                push('ok', `Disconnected ${c.name}.`);
                await loadActionConnections();
              })
            }
          />
        )}

        {tab === 'settings' && (
          <SettingsPanel
            meta={meta}
            profile={profile}
            projects={projects}
            priorities={priorities}
            notifications={notifications}
            onNotificationsChange={changeNotifications}
            onProfileChange={changeProfile}
            onCreateProject={createProject}
            onUpdateProject={async (id, patch) => {
              try {
                await api.updateProject(id, patch);
                await Promise.all([loadSupporting(), loadTasks(filters)]);
              } catch (err) {
                fail(err);
              }
            }}
            onDeleteProject={async (id) => {
              try {
                await api.deleteProject(id);
                await Promise.all([loadSupporting(), loadTasks(filters)]);
              } catch (err) {
                fail(err);
              }
            }}
            onCreatePriority={async (name, weight) => {
              try {
                await api.createPriority({ name, weight });
                await loadSupporting();
              } catch (err) {
                fail(err);
              }
            }}
            onUpdatePriority={async (id, patch) => {
              try {
                await api.updatePriority(id, patch);
                await Promise.all([loadSupporting(), loadTasks(filters)]);
              } catch (err) {
                fail(err);
              }
            }}
            onDeletePriority={async (id) => {
              try {
                await api.deletePriority(id);
                await Promise.all([loadSupporting(), loadTasks(filters)]);
              } catch (err) {
                fail(err);
              }
            }}
          />
        )}

        {tab === 'billing' && (
          <BillingPanel
            billing={user.billing}
            fetchPlans={api.billingPlans}
            fetchTransactions={api.billingTransactions}
            checkout={api.billingCheckout}
            openPortal={api.billingPortal}
            cancel={api.billingCancel}
            resume={api.billingResume}
            onBillingChange={(updated) => onUserChange({ ...user, billing: updated })}
            push={push}
          />
        )}

        {tab === 'admin' && user.isAdmin && <AdminPanel currentUserId={user.id} onImpersonate={impersonate} push={push} />}
      </main>

      {editorTask !== undefined && (
        <TaskEditor
          task={editorTask}
          meta={meta}
          projects={projects}
          onCreateProject={(name) => createProject(name)}
          linkCandidates={tasks}
          saving={savingTask}
          error={taskError}
          warnings={taskWarnings}
          onClose={() => setEditorTask(undefined)}
          onSave={saveTask}
          onDelete={editorTask ? removeTask : undefined}
          onTogglePin={
            editorTask
              ? () =>
                  mutateOpenTask(
                    () =>
                      api
                        .updateTask(editorTask.id, { pinned: !editorTask.pinned })
                        .then((r) => r.task),
                    editorTask.pinned ? 'Unpinned.' : 'Pinned to the top.'
                  )
              : undefined
          }
          onSnooze={
            editorTask
              ? (until) =>
                  mutateOpenTask(
                    () => api.snoozeTask(editorTask.id, until),
                    until ? 'Snoozed.' : 'Back in the list.'
                  )
              : undefined
          }
          onAddLink={
            editorTask
              ? (toTaskId: string, type: LinkType, syncEnabled: boolean) =>
                  mutateOpenTask(
                    () => api.addLink(editorTask.id, toTaskId, type, syncEnabled),
                    syncEnabled ? 'Linked & syncing.' : 'Linked.'
                  )
              : undefined
          }
          onRemoveLink={
            editorTask
              ? (linkId: string) =>
                  mutateOpenTask(async () => {
                    await api.deleteLink(linkId);
                    return api.viewTask(editorTask.id);
                  }, 'Link removed.')
              : undefined
          }
          docProviders={docProviders}
          onCreateArtifact={
            editorTask
              ? (provider: ActionProviderId, shareWithOrg: boolean) =>
                  mutateOpenTask(
                    () => api.createArtifact(editorTask.id, provider, shareWithOrg).then((r) => r.task),
                    'Document created.'
                  )
              : undefined
          }
        />
      )}

      {createExternalOpen && (
        <CreateExternalTaskModal
          connections={connections}
          providers={providers}
          fetchTargets={api.createTargets}
          onCreate={(connectionId, body) => api.createExternalTask(connectionId, body)}
          onCreated={onExternalTaskCreated}
          onClose={() => setCreateExternalOpen(false)}
        />
      )}

      {connFormOpen && providers.length > 0 && (
        <ConnectionForm
          providers={providers}
          connection={connBeingEdited}
          onClose={() => setConnFormOpen(false)}
          onConnectOAuth={connectConnectionOAuth}
          onSaved={async (saved, created) => {
            setConnFormOpen(false);
            push('ok', created ? `Connected “${saved.name}”.` : `Saved “${saved.name}”.`);
            await loadSupporting();
            if (created) {
              setBusyConnectionId(saved.id);
              try {
                const report = await api.syncConnection(saved.id);
                push(
                  report.ok ? 'ok' : 'error',
                  report.ok ? `Pulled ${report.fetched} item(s) from ${saved.name}.` : `${saved.name}: ${report.error}`
                );
              } catch (err) {
                fail(err);
              } finally {
                setBusyConnectionId(null);
              }
              await Promise.all([loadSupporting(), loadTasks(filters)]);
            }
          }}
        />
      )}

      {tourOpen && (
        <OnboardingTour
          hasConnections={connections.length > 0}
          onGoToSystems={() => {
            setTab('connections');
            setTourOpen(false);
          }}
          onCreateTask={() => {
            setEditorTask(null);
            setTourOpen(false);
          }}
          onGoToPriorities={() => {
            setTab('settings');
            setTourOpen(false);
          }}
          onFinish={finishOnboarding}
          onSkip={finishOnboarding}
        />
      )}

      <ToastStack toasts={toasts} dismiss={dismiss} />
    </div>
  );
}
